import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { X, History, RotateCcw } from "lucide-react";

const overlayStyle = { position: "fixed", inset: 0, background: "rgba(10,10,10,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 200 };
const modalStyle = { background: "#fff", borderRadius: 12, padding: 18, width: "100%", maxWidth: 720, maxHeight: "88vh", overflowY: "auto" };

/** Fix AE (2026-09-22) — CAMBIO 3: "Turnos borrados recientemente".
 * Lista los borrados de cronograma de los últimos 30 días (filtrable por
 * objetivo y sede) y permite restaurarlos uno por uno o en lote, tal cual
 * estaban. Los datos salen de la auditoría (schedule_assignment_deletions). */
export function ScheduleDeletionsPanel({ filterClient, filterSite, clients, onRestored, onClose }) {
  const [panelClient, setPanelClient] = useState(filterClient || "");
  const [panelSite, setPanelSite] = useState(filterSite || "");
  const [panelSites, setPanelSites] = useState([]);
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setRows(null);
    try {
      const params = {};
      if (panelClient) params.client_id = panelClient;
      if (panelSite) params.site_id = panelSite;
      const { data } = await api.get("/schedule/deletions", { params });
      setRows(data || []);
      setSelected([]);
    } catch (e) { toast.error(formatApiError(e)); setRows([]); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [panelClient, panelSite]);
  useEffect(() => {
    setPanelSite("");
    if (!panelClient) { setPanelSites([]); return; }
    api.get(`/clients/${panelClient}/sites`).then(({ data: s }) => setPanelSites(s || [])).catch(() => setPanelSites([]));
  }, [panelClient]);

  const restorable = useMemo(() => (rows || []).filter((r) => !r.restored_at), [rows]);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected((s) => (s.length === restorable.length ? [] : restorable.map((r) => r.id)));

  const restore = async (ids) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/schedule/deletions/restore", { ids });
      toast.success(`${res.restored} turno(s) restaurado(s)`);
      if (res.skipped_already_restored > 0) toast.info(`${res.skipped_already_restored} ya estaba(n) restaurado(s).`);
      if (res.skipped_conflict > 0) toast.error(`${res.skipped_conflict} ya existe(n) de nuevo en el cronograma — no se duplicaron.`);
      await load();
      if (onRestored) await onRestored();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose} data-testid="schedule-deletions-overlay">
      <div style={modalStyle} onClick={(e) => e.stopPropagation()} data-testid="schedule-deletions-modal">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <div style={{ fontSize: 15, fontWeight: 900, color: "#0A0A0A" }}>Turnos borrados recientemente</div>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#0A0A0A" }} data-testid="schedule-deletions-close"><X size={18} /></button>
        </div>
        <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginBottom: 12 }}>
          Borrados de los últimos 30 días (en lote o con "Quitar"). Restaurarlos los vuelve a crear tal cual estaban.
        </div>

        <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          <div className="field" style={{ margin: 0, minWidth: 200 }}>
            <label>Objetivo</label>
            <select value={panelClient} onChange={(e) => setPanelClient(e.target.value)} data-testid="schedule-deletions-filter-client">
              <option value="">Todos los objetivos</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {panelSites.length > 0 && (
            <div className="field" style={{ margin: 0, minWidth: 170 }}>
              <label>Sede</label>
              <select value={panelSite} onChange={(e) => setPanelSite(e.target.value)} data-testid="schedule-deletions-filter-site">
                <option value="">Todas las sedes</option>
                {panelSites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
        </div>

        {!rows && <div className="skel" style={{ height: 120 }} />}
        {rows && rows.length === 0 && (
          <div style={{ fontSize: 13, color: "var(--invict-text-muted)" }} data-testid="schedule-deletions-empty">No hay turnos borrados en los últimos 30 días para este filtro.</div>
        )}
        {rows && rows.length > 0 && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }} data-testid="schedule-deletions-select-all">
                <input type="checkbox" checked={restorable.length > 0 && selected.length === restorable.length} onChange={toggleAll} />
                Seleccionar todos ({restorable.length})
              </label>
              <button className="btn-primary" style={{ padding: "8px 14px", fontSize: 12 }} disabled={busy || selected.length === 0}
                onClick={() => restore(selected)} data-testid="schedule-deletions-restore-selected">
                <RotateCcw size={12} style={{ marginRight: 6, verticalAlign: "-2px" }} />
                {busy ? "Restaurando..." : `Restaurar ${selected.length} turno(s)`}
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid="schedule-deletions-list">
              {rows.map((r) => {
                const a = r.assignment || {};
                const restored = !!r.restored_at;
                return (
                  <div key={r.id} data-testid={`schedule-deletions-row-${r.id}`}
                    style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", border: "1px solid var(--invict-bone-3)", borderRadius: 4, background: restored ? "var(--invict-bone-2)" : "#fff", opacity: restored ? 0.65 : 1 }}>
                    <input type="checkbox" disabled={restored || busy} checked={selected.includes(r.id)} onChange={() => toggle(r.id)} style={{ marginTop: 3 }} data-testid={`schedule-deletions-check-${r.id}`} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: "#0A0A0A" }}>
                        {a.user_name} · {a.date?.slice(8, 10)}/{a.date?.slice(5, 7)}/{a.date?.slice(0, 4)}
                        {a.is_day_off
                          ? <span className="badge gray" style={{ marginLeft: 6, fontSize: 9 }}>FRANCO</span>
                          : <span style={{ fontWeight: 600, color: "var(--invict-text-muted)" }}> · {a.time_from}–{a.time_to} hs</span>}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 2 }}>
                        {a.client_name}{a.site_name ? ` · ${a.site_name}` : ""}{a.notes ? ` · "${a.notes}"` : ""}
                      </div>
                      <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 2 }}>
                        Borrado por <b>{r.deleted_by_name}</b> ({r.source === "bulk-delete" ? "borrado en lote" : "quitar individual"}) el {new Date(r.deleted_at).toLocaleString("es-AR")}
                        {a.created_at && <> · turno creado el {new Date(a.created_at).toLocaleString("es-AR")}</>}
                      </div>
                      {restored && (
                        <div style={{ fontSize: 10, color: "#16a34a", fontWeight: 700, marginTop: 2 }} data-testid={`schedule-deletions-restored-${r.id}`}>
                          Restaurado por {r.restored_by_name} el {new Date(r.restored_at).toLocaleString("es-AR")}
                        </div>
                      )}
                    </div>
                    {!restored && (
                      <button className="btn-ghost" style={{ padding: "5px 10px", fontSize: 11 }} disabled={busy}
                        onClick={() => restore([r.id])} data-testid={`schedule-deletions-restore-${r.id}`}>Restaurar</button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
